import React, { useContext, useState, useEffect, useRef, useMemo } from "react";
import { DataContext } from "../contexts/DataContext";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faRocket,
  faGlobe,
  faUsers,
  faDollarSign,
  faPercentage,
  faClock,
  faCheckCircle,
  faShip,
  faPlane,
  faTruck,
  faClipboardList,
  faFileInvoice,
  faCog,
  faIdCard,
  faChartPie,
  faChartLine,
  faExclamationTriangle,
  faProcedures,
  faFilter,
  faSync,
  faExpand,
  faSearch,
  faTimes,
  faCalendarAlt,
  faChevronDown,
  faChevronUp,
  faSpinner,
  faAtom,
  faArrowRight,
  faCompress,
} from "@fortawesome/free-solid-svg-icons";

import GlobalHeader from "./GlobalHeader";
import HolographicKPICard from "./HolographicKPICard";

// Import chart components
import EnhancedReferencesPerEntryPointBarChart from "./EnhancedReferencesPerEntryPointBarChart";
import EnhancedReferencesPerStatusBarChart from "./EnhancedReferencesPerStatusBarChart";
import EnhancedAttentionTimeBarChart from "./EnhancedAttentionTimeBarChart";
import ProcessingEfficiencyMatrixChart from "./ProcessingEfficiencyMatrixChart";
import EnhancedReferencesPerDocumentTypeBarChart from "./EnhancedReferencesPerDocumentTypeBarChart";
import EnhancedReferencesPerOperationTypeDonutChart from "./EnhancedReferencesPerOperationTypeDonutChart";
import EnhancedReferencesPerEntryPointAndLicenseBarChart from "./EnhancedReferencesPerEntryPointAndLicenseBarChart";
import ReferencesDistributionByCarrierBarChart from "./ReferencesDistributionByCarrierBarChart";
import DocTimeBarChart from "./DocTimeBarChart";
import EnhancedAttentionDocumentTimesSeriesChart from "./EnhancedAttentionDocumentTimesSeriesChart";
import ProcessBottleneckAnalyzer from "./ProcessBottleneckAnalyzer";

// Filter imports
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import Select from "react-select";

const UltraStunningDashboardSimplified = () => {
  const { trafficData, isLoading, fetchTrafficData, setShouldFetchData } = useContext(DataContext);
  const { logout } = useAuth();
  const navigate = useNavigate();

  // State management
  const [filteredData, setFilteredData] = useState([]);
  const [visualMode, setVisualMode] = useState("cinematic");
  const [isImmersiveMode, setIsImmersiveMode] = useState(false);
  const [expandedChart, setExpandedChart] = useState(null);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedEntryPoints, setSelectedEntryPoints] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [dateRange, setDateRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: "selection",
    },
  ]);

  // Options for filters
  const [customerOptions, setCustomerOptions] = useState([]);
  const [entryPointOptions, setEntryPointOptions] = useState([]);
  const statusOptions = [
    { value: "Paid", label: "Paid" },
    { value: "Open", label: "Open" },
  ];

  // KPI Data
  const [kpiData, setKpiData] = useState({
    totalSessions: 0,
    totalUsers: 0,
    totalRevenue: 0,
    processingEfficiency: 0,
    activeOperations: 0,
    avgProcessingTime: 0,
    completedToday: 0,
    pendingOperations: 0,
    totalReferences: 0,
    seaFreight: 0,
    airFreight: 0,
    roadFreight: 0,
  });

  // Fetch and process data
  useEffect(() => {
    setShouldFetchData((prev) => ({ ...prev, fetchTrafficData: true }));
    fetchTrafficData();
  }, [fetchTrafficData, setShouldFetchData]);

  useEffect(() => {
    if (trafficData && trafficData.length > 0) {
      // Extract filter options
      const uniqueCustomers = Array.from(
        new Set(trafficData.map((item) => item.header.customer_name))
      )
        .filter(Boolean)
        .map((customer) => ({ value: customer, label: customer }));
      setCustomerOptions(uniqueCustomers);

      const uniqueEntryPoints = Array.from(
        new Set(
          trafficData
            .map((item) => item.header.pedimento?.pedimento_entry_point)
            .filter(Boolean)
        )
      ).map((entryPoint) => ({ value: entryPoint, label: entryPoint }));
      setEntryPointOptions(uniqueEntryPoints);

      calculateKPIs(trafficData);
      setFilteredData(trafficData);
    } else if (trafficData && trafficData.length === 0) {
      // Handle empty data case - show dashboard with no data message
      setFilteredData([]);
      setCustomerOptions([]);
      setEntryPointOptions([]);
      setKpiData({
        totalSessions: 0,
        totalUsers: 0,
        totalRevenue: 0,
        processingEfficiency: 0,
        activeOperations: 0,
        avgProcessingTime: 0,
        completedToday: 0,
        pendingOperations: 0,
        totalReferences: 0,
        seaFreight: 0,
        airFreight: 0,
        roadFreight: 0,
      });
    }
  }, [trafficData]);

  // Apply filters
  useEffect(() => {
    if (!trafficData) return;

    let filtered = trafficData;

    if (searchTerm) {
      filtered = filtered.filter((item) =>
        JSON.stringify(item.header).toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCustomers.length) {
      filtered = filtered.filter((item) =>
        selectedCustomers.some((selected) => selected.value === item.header.customer_name)
      );
    }

    if (selectedEntryPoints.length) {
      filtered = filtered.filter((item) =>
        selectedEntryPoints.some(
          (selected) => selected.value === item.header.pedimento?.pedimento_entry_point
        )
      );
    }

    if (selectedStatus.length) {
      filtered = filtered.filter((item) => {
        const status = item.header.detail?.traffic_data?.status === "Pagado" ? "Paid" : "Open";
        return selectedStatus.some((selected) => selected.value === status);
      });
    }

    const { startDate, endDate } = dateRange[0];
    if (startDate && endDate) {
      filtered = filtered.filter((item) => {
        const dateInit = item.header.detail?.dates_data?.date_init
          ? new Date(item.header.detail.dates_data.date_init)
          : null;
        return dateInit && dateInit >= startDate && dateInit <= endDate;
      });
    }

    setFilteredData(filtered);
    calculateKPIs(filtered);
  }, [
    trafficData,
    searchTerm,
    selectedCustomers,
    selectedEntryPoints,
    selectedStatus,
    dateRange,
  ]);

  const calculateKPIs = (data) => {
    const totalSessions = data.length;
    const totalUsers = new Set(data.map((item) => item.header.customer_rfc)).size;
    const totalRevenue = data.length * 2500;

    const paidItems = data.filter(
      (item) => item.header.detail?.traffic_data?.status === "Pagado"
    ).length;
    const processingEfficiency = totalSessions > 0 ? (paidItems / totalSessions) * 100 : 0;

    const activeOperations = totalSessions - paidItems;
    const avgProcessingTime = 4.2;
    const completedToday = Math.floor(totalSessions * 0.1);
    const pendingOperations = activeOperations;

    // Calculate transport mode counts
    const totalReferences = data.length;
    const seaFreightEntryPoints = [160, 510, 430];
    const airFreightEntryPoints = [520, 521, 640];
    const roadFreightEntryPoints = [240];

    const seaFreight = data.filter(item =>
      seaFreightEntryPoints.includes(item.header.pedimento?.pedimento_entry_point)
    ).length;

    const airFreight = data.filter(item =>
      airFreightEntryPoints.includes(item.header.pedimento?.pedimento_entry_point)
    ).length;

    const roadFreight = data.filter(item =>
      roadFreightEntryPoints.includes(item.header.pedimento?.pedimento_entry_point)
    ).length;

    setKpiData({
      totalSessions,
      totalUsers,
      totalRevenue,
      processingEfficiency,
      activeOperations,
      avgProcessingTime,
      completedToday,
      pendingOperations,
      totalReferences,
      seaFreight,
      airFreight,
      roadFreight,
    });
  };

  const handleRefreshData = () => {
    setShouldFetchData((prev) => ({
      ...prev,
      fetchTrafficData: true,
    }));
    fetchTrafficData();
  };

  const clearAllFilters = () => {
    setSelectedCustomers([]);
    setSelectedEntryPoints([]);
    setSelectedStatus([]);
    setSearchTerm("");
    setDateRange([
      {
        startDate: null,
        endDate: null,
        key: "selection",
      },
    ]);
  };

  const hasActiveFilters =
    selectedCustomers.length > 0 ||
    selectedEntryPoints.length > 0 ||
    selectedStatus.length > 0 ||
    (dateRange[0].startDate && dateRange[0].endDate) ||
    searchTerm.length > 0;

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: "rgba(255, 255, 255, 0.05)",
      borderColor: state.isFocused ? "#00D9FF" : "rgba(255, 255, 255, 0.1)",
      boxShadow: state.isFocused ? "0 0 0 1px #00D9FF" : "none",
      borderRadius: "0.5rem",
      backdropFilter: "blur(12px)",
      color: "#ffffff",
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: "rgba(0, 0, 0, 0.9)",
      backdropFilter: "blur(20px)",
      border: "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: "0.5rem",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#00D9FF"
        : state.isFocused
        ? "rgba(0, 217, 255, 0.1)"
        : "transparent",
      color: "#ffffff",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: "rgba(0, 217, 255, 0.2)",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: "#00D9FF",
    }),
  };

  const actionButtons = (
    <>
      <motion.button
        onClick={handleRefreshData}
        className="flex items-center space-x-2 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 text-white shadow-lg shadow-emerald-500/25"
        disabled={isLoading}
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
      >
        <FontAwesomeIcon
          icon={isLoading ? faSpinner : faSync}
          className={isLoading ? "animate-spin" : ""}
        />
        <span>{isLoading ? "Refreshing..." : "Refresh Data"}</span>
      </motion.button>

      <motion.button
        onClick={() => setVisualMode(prev => {
          const modes = ["cinematic", "holographic", "matrix"];
          const currentIndex = modes.indexOf(prev);
          return modes[(currentIndex + 1) % modes.length];
        })}
        className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 text-white shadow-lg shadow-purple-500/25"
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
      >
        <FontAwesomeIcon icon={faAtom} />
        <span className="capitalize">{visualMode} Mode</span>
      </motion.button>

      <motion.button
        onClick={() => setIsImmersiveMode(!isImmersiveMode)}
        className="flex items-center space-x-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 px-6 py-3 rounded-xl text-sm font-medium transition-all duration-300 text-white shadow-lg shadow-cyan-500/25"
        whileHover={{ scale: 1.05, y: -2 }}
        whileTap={{ scale: 0.95 }}
      >
        <FontAwesomeIcon icon={isImmersiveMode ? faCompress : faExpand} />
        <span>{isImmersiveMode ? "Exit" : "Enter"} Immersive</span>
      </motion.button>
    </>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0E27] via-[#1a1a2e] to-[#16213e] flex items-center justify-center">
        <motion.div
          className="text-center"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
        >
          <motion.div
            className="relative mb-8"
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <div className="w-32 h-32 border-4 border-cyan-500/30 border-t-cyan-400 rounded-full mx-auto" />
            <div
              className="absolute inset-4 border-4 border-purple-500/30 border-b-purple-400 rounded-full"
              style={{ animation: "spin 2s linear infinite reverse" }}
            />
          </motion.div>
          <h1 className="text-6xl font-bold text-white mb-4 bg-gradient-to-r from-cyan-400 via-purple-400 to-orange-400 bg-clip-text text-transparent">
            Ultra Dashboard
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Initializing next-generation analytics experience...
          </p>
          <div className="flex justify-center space-x-4">
            {["Quantum Processing", "Neural Networks", "Data Synthesis"].map((text, i) => (
              <motion.div
                key={text}
                className="px-4 py-2 bg-white/5 rounded-lg border border-cyan-500/30 backdrop-blur-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.2 }}
              >
                <span className="text-cyan-400 text-sm">{text}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0E27] via-[#1a1a2e] to-[#16213e] overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/10 via-purple-500/10 to-orange-500/10 animate-pulse" />
        {/* Simple particle effect using CSS */}
        <div className="absolute inset-0 overflow-hidden">
          {Array.from({ length: 50 }).map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>
      </div>

      <div className="relative z-10 h-screen overflow-y-auto">
        {/* Global Header */}
        <GlobalHeader
          title="Ultra Control Tower"
          icon={faRocket}
          actionButtons={actionButtons}
          className="backdrop-blur-xl bg-black/20 border-b border-white/10"
        />

        {/* Hero Section */}
        <section className="min-h-screen flex items-center justify-center px-6">
          <div className="max-w-7xl mx-auto w-full">
            {/* Main Title */}
            <motion.div
              className="text-center mb-8"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <h1 className="text-6xl font-bold text-white mb-4 tracking-tight">
                ULTRA
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-purple-400 to-orange-400">
                  VISION
                </span>
              </h1>
              <motion.p
                className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8 }}
              >
                Experience the future of logistics analytics with
                <span className="text-cyan-400"> holographic insights</span>,
                <span className="text-purple-400"> liquid data flows</span>, and
                <span className="text-orange-400"> immersive visualizations</span>
              </motion.p>
            </motion.div>

            {/* KPI Display */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <HolographicKPICard
                title="Total Sessions"
                value={kpiData.totalSessions}
                previousValue={kpiData.totalSessions * 0.9}
                icon={faUsers}
                color="cyan"
                size="large"
                holographicIntensity={1.2}
                animationDuration={2000}
              />
              <HolographicKPICard
                title="Active Users"
                value={kpiData.totalUsers}
                previousValue={kpiData.totalUsers * 0.95}
                icon={faGlobe}
                color="purple"
                size="large"
                holographicIntensity={1.2}
                animationDuration={2200}
              />
              <HolographicKPICard
                title="Revenue"
                value={kpiData.totalRevenue}
                previousValue={kpiData.totalRevenue * 0.92}
                icon={faDollarSign}
                color="orange"
                size="large"
                format="currency"
                holographicIntensity={1.2}
                animationDuration={2400}
              />
              <HolographicKPICard
                title="Efficiency"
                value={kpiData.processingEfficiency}
                previousValue={kpiData.processingEfficiency * 0.98}
                icon={faPercentage}
                color="green"
                size="large"
                format="percentage"
                suffix="%"
                holographicIntensity={1.2}
                animationDuration={2600}
              />
            </div>

            {/* Transport Mode KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <HolographicKPICard
                title="Total References"
                value={kpiData.totalReferences}
                previousValue={kpiData.totalReferences * 0.95}
                icon={faClipboardList}
                color="blue"
                size="large"
                holographicIntensity={1.1}
                animationDuration={2800}
              />
              <HolographicKPICard
                title="Sea Freight"
                value={kpiData.seaFreight}
                previousValue={kpiData.seaFreight * 0.92}
                icon={faShip}
                color="cyan"
                size="large"
                holographicIntensity={1.1}
                animationDuration={3000}
              />
              <HolographicKPICard
                title="Air Freight"
                value={kpiData.airFreight}
                previousValue={kpiData.airFreight * 0.88}
                icon={faPlane}
                color="purple"
                size="large"
                holographicIntensity={1.1}
                animationDuration={3200}
              />
              <HolographicKPICard
                title="Road Freight"
                value={kpiData.roadFreight}
                previousValue={kpiData.roadFreight * 0.96}
                icon={faTruck}
                color="green"
                size="large"
                holographicIntensity={1.1}
                animationDuration={3400}
              />
            </div>
          </div>
        </section>

        {/* Search and Filter Bar */}
        <div className="sticky top-20 z-40 backdrop-blur-xl bg-black/20 border-b border-white/10">
          <div className="max-w-7xl mx-auto p-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400 backdrop-blur-sm"
                    placeholder="Search across all metrics..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Filter Buttons */}
              <div className="flex gap-2">
                <motion.button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all ${
                    showFilters
                      ? "bg-cyan-500/20 text-cyan-400 border-cyan-400/50"
                      : "bg-white/5 text-gray-300 border-white/10"
                  } border backdrop-blur-sm`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FontAwesomeIcon icon={faFilter} />
                  <span>Filters</span>
                  <FontAwesomeIcon icon={showFilters ? faChevronUp : faChevronDown} />
                </motion.button>

                <motion.button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all ${
                    showDatePicker || (dateRange[0].startDate && dateRange[0].endDate)
                      ? "bg-purple-500/20 text-purple-400 border-purple-400/50"
                      : "bg-white/5 text-gray-300 border-white/10"
                  } border backdrop-blur-sm`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} />
                  <span>Date Range</span>
                </motion.button>

                <motion.button
                  onClick={clearAllFilters}
                  disabled={!hasActiveFilters}
                  className={`flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-medium transition-all border backdrop-blur-sm ${
                    hasActiveFilters
                      ? "bg-red-500/20 text-red-400 border-red-400/50 hover:bg-red-500/30"
                      : "bg-gray-500/10 text-gray-500 border-gray-500/20 cursor-not-allowed"
                  }`}
                  whileHover={hasActiveFilters ? { scale: 1.05 } : {}}
                  whileTap={hasActiveFilters ? { scale: 0.95 } : {}}
                >
                  <FontAwesomeIcon icon={faTimes} />
                  <span>Clear All</span>
                </motion.button>
              </div>
            </div>

            {/* Filter Dropdowns */}
            <AnimatePresence>
              {showFilters && (
                <motion.div
                  className="mt-6 p-6 bg-black/40 backdrop-blur-xl rounded-xl border border-white/10"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Customers
                      </label>
                      <Select
                        isMulti
                        value={selectedCustomers}
                        onChange={setSelectedCustomers}
                        options={customerOptions}
                        styles={customSelectStyles}
                        placeholder="Select customers..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Entry Points
                      </label>
                      <Select
                        isMulti
                        value={selectedEntryPoints}
                        onChange={setSelectedEntryPoints}
                        options={entryPointOptions}
                        styles={customSelectStyles}
                        placeholder="Select entry points..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Status
                      </label>
                      <Select
                        isMulti
                        value={selectedStatus}
                        onChange={setSelectedStatus}
                        options={statusOptions}
                        styles={customSelectStyles}
                        placeholder="Select status..."
                      />
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Date Picker */}
            <AnimatePresence>
              {showDatePicker && (
                <motion.div
                  className="mt-6 p-6 bg-black/40 backdrop-blur-xl rounded-xl border border-white/10"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <div className="flex justify-center">
                    <DateRangePicker
                      ranges={dateRange}
                      onChange={(ranges) => setDateRange([ranges.selection])}
                      showSelectionPreview={true}
                      moveRangeOnFirstSelection={false}
                      months={2}
                      direction="horizontal"
                      className="bg-black/20 rounded-lg"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Analytics Section */}
        <section className="px-6 py-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              className="text-center mb-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl font-bold text-white mb-4 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Advanced Analytics
              </h2>
              <p className="text-lg text-gray-300">
                Immersive data exploration with stunning visualizations
              </p>
            </motion.div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                { component: EnhancedReferencesPerEntryPointBarChart, title: "Entry Point Analysis", icon: faShip },
                { component: EnhancedReferencesPerStatusBarChart, title: "Status Distribution", icon: faCheckCircle },
                { component: EnhancedReferencesPerDocumentTypeBarChart, title: "Document Types", icon: faFileInvoice },
                { component: EnhancedReferencesPerOperationTypeDonutChart, title: "Operation Types", icon: faChartPie },
                { component: EnhancedAttentionTimeBarChart, title: "Processing Times", icon: faClock },
                { component: DocTimeBarChart, title: "Document Times", icon: faChartLine },
                { component: EnhancedReferencesPerEntryPointAndLicenseBarChart, title: "Entry Point & License", icon: faIdCard },
                { component: ReferencesDistributionByCarrierBarChart, title: "Carrier Distribution", icon: faTruck },
                { component: EnhancedAttentionDocumentTimesSeriesChart, title: "Traffic Trends", icon: faChartLine },
                { component: ProcessingEfficiencyMatrixChart, title: "Efficiency Matrix", icon: faGlobe },
                { component: ProcessBottleneckAnalyzer, title: "Bottleneck Analysis", icon: faExclamationTriangle },
              ].map((chart, index) => (
                <motion.div
                  key={index}
                  className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6 hover:bg-white/10 transition-all duration-300"
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  whileHover={{ scale: 1.02, y: -5 }}
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <div className="p-3 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-xl">
                        <FontAwesomeIcon icon={chart.icon} className="text-cyan-400 text-xl" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">{chart.title}</h3>
                    </div>
                    <motion.button
                      className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white/60 hover:text-white"
                      onClick={() => setExpandedChart({ ...chart, index })}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FontAwesomeIcon icon={faExpand} />
                    </motion.button>
                  </div>
                  <div className="h-64">
                    <chart.component
                      data={filteredData}
                      isLoading={isLoading}
                      height="100%"
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Visual Mode Indicator */}
        <motion.div
          className="fixed bottom-8 left-8 z-40"
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1 }}
        >
          <div className="bg-black/70 backdrop-blur-xl rounded-xl border border-white/20 p-4">
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={faAtom} className="text-cyan-400" />
              <div>
                <div className="text-white font-semibold capitalize">{visualMode} Mode</div>
                <div className="text-gray-400 text-xs">Ultra Experience Active</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Expanded Chart Modal */}
      <AnimatePresence>
        {expandedChart && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setExpandedChart(null)}
          >
            <motion.div
              className="w-full max-w-6xl h-full max-h-[90vh] bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-white/20 p-6 overflow-hidden"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-xl">
                    <FontAwesomeIcon icon={expandedChart.icon} className="text-cyan-400 text-xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{expandedChart.title}</h2>
                    <p className="text-gray-400">Expanded View - Full Analytics</p>
                  </div>
                </div>
                <motion.button
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white/60 hover:text-white"
                  onClick={() => setExpandedChart(null)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </motion.button>
              </div>

              {/* Expanded Chart Content */}
              <div className="h-[calc(100%-120px)] bg-white/5 rounded-xl p-4">
                <expandedChart.component
                  data={filteredData}
                  isLoading={isLoading}
                  height="100%"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UltraStunningDashboardSimplified;